/**
 *  @fileOverview Autocomplete module make autocompleter a reusable module
 *
 *  @author       Aaron Chapman / Chris Hill
 *
 */

function AutocompleteModule (axios, accessibleAutocomplete, Utilities) {
  /**
   * Base URL for the Funnelback JSON search API via rest asset
   */
  const baseURL = 'https://ecu-search.clients.funnelback.com/s/suggest.json'

  /**
   * @description Initialises autocomplete.
   * https://www.npmjs.com/package/autocompleter
   *
   * @param {string} input - Input element.
   * @param {string} container - Container element selector.
   * @param {string} inputClass - Class to add to input element for styles.
   * @param {Array} collections - Funnelback collections to search, defaults to ecu-web and sub collections.
   *
   * @example
   * //
   * @returns {boolean} // null.
   */

  this.init = function ({
    input,
    container,
    inputClass = 'searchInput',
    name = 'input-autocomplete',
    value = '',
    collections = ['ecu-web', 'ecu-fs-courses', 'ecu-web-staffdir', 'ecu-knowledge']
  } = {}) {
    let options = {
      element: document.querySelector(container),
      id: input,
      source: Utilities.debounce(async (query, populateResults) => {
        const res = await getSuggestions(query, collections)
        populateResults(res)
      }, 300),
      name: name,
      autoselect: false,
      experimentalAllowAnyInput: true,
      templates: {
        inputValue: function (result) {
          return result && result.label
        },
        suggestion: function (result) {
          let group = ''
          let returnString = result && '<span class="autocomplete__title">' + result.label + '</span>'

          if (result.group) {
            group = result.group

            returnString += '<span class="autocomplete__category base__heading base__heading--xxs">' + group + '</span>'
          }

          if (typeof result.url !== 'undefined') {
            returnString += '<span class="visuallyHidden">Link to page</span>'
          }

          return returnString
        }
      },
      onConfirm: function (result) {
        let allowLink = true
        if (typeof result !== 'undefined') {
          if (typeof result.url !== 'undefined') {
            if (typeof result.group !== 'undefined' && result.group === 'Staff') {
              allowLink = false
            }
            if (allowLink) {
              location.assign(result.url)
            } else {
              // This is duplicate code, it probably needs a refactor at some point
              let input = document.querySelector('#' + this.id)
              input.value = result.value
              let theForm = input.form
              if (container === '#search__container') {
                const domEvent = document.createEvent('Event')
                domEvent.initEvent('submit', false, true)
                theForm.dispatchEvent(domEvent)
              } else {
                theForm.submit()
              }
            }
          } else {
            // This is duplicate code, it probably needs a refactor at some point
            let input = document.querySelector('#' + this.id)
            input.value = result.value
            let theForm = input.form
            if (container === '#search__container') {
              const domEvent = document.createEvent('Event')
              domEvent.initEvent('submit', false, true)
              theForm.dispatchEvent(domEvent)
            } else {
              theForm.submit()
            }
          }
        }
      },
      displayMenu: 'overlay',
      minLength: 1,
      showNoOptionsFound: false
    }

    if (value !== '') {
      options.defaultValue = value
    }

    accessibleAutocomplete(options)

    const searchInput = document.querySelector('#' + input)

    searchInput.type = 'search'
    searchInput.classList.add(inputClass)
  }

  async function getSuggestions (query, collections) {
    query = query.toLowerCase()

    try {
      /**
       * Use ternary comparision to see if we need to retrieve suggestions for collection
       * Aaron var = if in array ? do axios : else do null :D
       */
      const allSuggestions = collections.includes('ecu-web') ? await axios.get(baseURL + `?collection=ecu-web&fmt=json%2B%2B&show=5&sort=0&partial_query=${encodeURIComponent(query)}`) : null
      const courseSuggestions = collections.includes('ecu-fs-courses') ? await axios.get(baseURL + `?collection=ecu-fs-courses&fmt=json%2B%2B&profile=autocomplete&show=5&sort=0&partial_query=${encodeURIComponent(query)}`) : null
      const staffSuggestions = collections.includes('ecu-web-staffdir') ? await axios.get(baseURL + `?collection=ecu-web-staffdir&fmt=json%2B%2B&profile=autocomplete&show=5&sort=0&partial_query=${encodeURIComponent(query)}`) : null
      const askUsSuggestions = collections.includes('ecu-knowledge') ? await axios.get(baseURL + `?collection=ecu-knowledge&fmt=json%2B%2B&profile=autocomplete&show=5&sort=0&partial_query=${encodeURIComponent(query)}`) : null

      try {
        const [all, course, staff, askUs] = await Promise.all([allSuggestions, courseSuggestions, staffSuggestions, askUsSuggestions])

        let options = []

        /**
         * Iterate the collections results and output. Check for null values.
         */

        if (all !== null) {
          let tempOptions = []
          if (course !== null) {
            tempOptions = tempOptions.concat(course.data)
          }
          if (staff !== null) {
            tempOptions = tempOptions.concat(staff.data)
          }
          if (askUs !== null) {
            tempOptions = tempOptions.concat(askUs.data)
          }
          let filteredAll = all.data.filter(item => {
            let keep = true
            let i = 0

            if (item.disp === null) {
              keep = false
            }
            while (keep === true && i < tempOptions.length) {
              if (tempOptions[i].disp.title !== undefined) {
                if (item.disp.toLowerCase().includes(tempOptions[i].disp.title.toLowerCase())) {
                  keep = false
                }
              }
              i++
            }
            return keep
          })
          filteredAll.forEach((r) => {
            options.push(
              { label: r.disp, value: r.disp }
            )
          })
        }

        if (course !== null) {
          course.data.forEach((r) => {
            if (typeof r.disp.title !== 'undefined') {
              options.push(
                {
                  label: r.disp.title,
                  value: r.disp.title,
                  group: 'Courses',
                  url: r.disp.displayUrl
                }
              )
            }
          })
        }

        if (staff !== null) {
          staff.data.forEach((r) => {
            if (typeof r.disp.title !== 'undefined') {
              if (typeof r.disp.metaData !== 'undefined' && r.disp.metaData.hasOwnProperty('B')) {
                options.push(
                  { label: r.disp.title, value: r.disp.title, group: 'Staff', url: r.disp.metaData.B }
                )
              } else {
                options.push(
                  { label: r.disp.title, value: r.disp.title, group: 'Staff' }
                )
              }
            }
          })
        }

        if (askUs !== null) {
          askUs.data.forEach((r) => {
            if (typeof r.disp.title !== 'undefined') {
              options.push(
                {
                  label: r.disp.title,
                  value: r.disp.title,
                  group: 'Ask Us',
                  url: r.disp.displayUrl
                }
              )
            }
          })
        }

        return options
      } catch (err) {
        console.error(err)
      }
    } catch (err) {
      console.error(err)
    }
  }
}

module.exports = AutocompleteModule
